'use client';

import styles from './QuestionnaireFooter.module.css';
import {
  Alert,
  Button,
  ButtonGroup,
  Text,
  Dialog,
  Flex,
  Separator,
} from '@/_components';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { submitQuestionnaire } from '@/_api/client-questionnaires';
import { useContext, useMemo, useState, type FC } from 'react';
import { useRouter } from 'next/navigation';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { SuccessQuestionnaireSubmittedImage } from './images/SuccessQuestionnaireSubmittedImage';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import type {
  QuestionnaireFooterProps,
  NextQuestionnaire,
} from './QuestionnaireFooter.types';
import type { Questionnaire } from '@/_types/models/questionnaire';
import type { Section } from '@/_types/models/sections';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { QuestionnaireTemplate } from '@/_types/models/questionnaireTemplate';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const QuestionnaireFooter: FC<QuestionnaireFooterProps> = ({
  isLoading: isQuestionnaireLoading,
  mode,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isConfirming, setConfirming] = useState<boolean>(false);
  const [isSubmitted, setSubmitted] = useState<boolean>(false);
  const { user, token, selectedCompanyHash } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);
  const { addToast } = useContext(ToastContext);
  const [nextQuestionnaire, setNextQuestionnaire] = useState<NextQuestionnaire>(
    {},
  );
  const {
    setReviewed,
    isReviewed,
    selectedSection,
    selectedSections,
    selectedQuestionnaire,
    isDisabled,
    isQuestionnairePending,
    setShowIncompleteNavigation,
  } = useContext(ClientQuestionnaireContext);

  const {
    isPreviewQuestionnairePending,
    previewSection,
    previewQuestionnaire,
    previewCompanyId,
    previewSections,
    setShowIncompletePreviewNavigation,
  } = useContext(PreviewContext);

  const { previewTemplate, previewTemplateSection, previewTemplateSections } =
    useContext(UserPreviewContext);

  const isTemplatePreview: boolean = mode === 'admin-template';

  const triggeredSections: Array<Section> = useMemo(() => {
    if (mode === 'admin-preview') return previewSections;
    if (mode === 'admin-template') return previewTemplateSections;

    return selectedSections;
  }, [mode, previewSections, previewTemplateSections, selectedSections]);

  const section: Partial<Section> | undefined = useMemo(() => {
    if (mode === 'admin-preview') return previewSection;
    if (mode === 'admin-template') return previewTemplateSection;

    return selectedSection;
  }, [mode, previewSection, previewTemplateSection, selectedSection]);

  const questionnaire: Questionnaire | QuestionnaireTemplate | undefined =
    useMemo(() => {
      if (mode === 'admin-preview') return previewQuestionnaire;
      if (mode === 'admin-template') return previewTemplate;

      return selectedQuestionnaire;
    }, [mode, previewQuestionnaire, previewTemplate, selectedQuestionnaire]);

  const isPending: boolean = useMemo(() => {
    if (mode === 'admin-preview') return isPreviewQuestionnairePending;
    if (mode === 'admin-template') return false;

    return isQuestionnairePending;
  }, [mode, isQuestionnairePending, isPreviewQuestionnairePending]);

  const companyId: number | null = useMemo(() => {
    if (mode === 'admin-preview') return previewCompanyId!;
    if (mode === 'admin-template') return null;

    return user?.default_company_id!;
  }, [mode, previewCompanyId, user?.default_company_id]);

  const hasReviewSection: boolean = useMemo(
    () => !!triggeredSections.some((section) => section.section_type === 4),
    [triggeredSections],
  );

  const isReviewedAndReviewNeeded: boolean = Boolean(
    hasReviewSection ? isReviewed : true,
  );

  const sectionsCount: number = triggeredSections.length ?? 0;
  const hasSections: boolean = !!sectionsCount;

  const isSubmitSection: boolean = Boolean(
    section?.section_type === 4 || sectionsCount <= 1,
  );

  const firstSectionId: number = triggeredSections[0]?.id || -1;
  const currentSectionIndex: number =
    triggeredSections.findIndex((s) => s.id === section?.id) || 0;

  const { push } = useRouter();

  const isPreviousDisabled: boolean =
    !hasSections ||
    (hasSections && section?.id === questionnaire?.sections?.[0]?.id);

  const lastSection: Section | undefined =
    triggeredSections[triggeredSections.length - 1];

  const isNextEnabled: boolean = Boolean(
    hasSections && section && section?.id !== lastSection?.id,
  );

  const isSubmittable: boolean = isSubmitSection
    ? isTemplatePreview
      ? false
      : isReviewedAndReviewNeeded
    : isNextEnabled;

  const onClientPreviousSection = () => {
    if (isPreviousDisabled) return;

    const previousSectionId: number =
      triggeredSections[currentSectionIndex - 1]?.id || firstSectionId;

    const _getPreviewUrl = (): string => {
      if (mode === 'admin-preview') {
        return `/preview/${previewCompanyId}/questionnaire/${questionnaire?.id}?sectionId=${previousSectionId}`;
      }

      if (mode === 'admin-template') {
        return `/preview/template/${previewTemplate?.id}?sectionId=${previousSectionId}`;
      }

      return `/questionnaire/${selectedCompanyHash}?questionnaireId=${questionnaire?.id}&sectionId=${previousSectionId}`;
    };

    const previewUrl: string = _getPreviewUrl();

    return push(previewUrl);
  };

  const onClientNextSection = () => {
    if (!isNextEnabled) return;

    const hasFirstSectionRequiredIssue: boolean = Boolean(
      mode !== 'admin-template' &&
        !!preferences?.first_section_required &&
        currentSectionIndex === 0 &&
        !triggeredSections?.[0]?.marked_completed &&
        triggeredSections?.[0]?.section_type !== 0,
    );

    if (hasFirstSectionRequiredIssue) {
      addToast('error', 'Please complete the first section before proceeding.');
      return;
    }

    const nextSectionId: number | undefined =
      triggeredSections[currentSectionIndex + 1]?.id || firstSectionId;

    const _getNextUrl = (): string => {
      if (mode === 'admin-preview') {
        return `/preview/${previewCompanyId}/questionnaire/${questionnaire?.id}?sectionId=${nextSectionId}`;
      }

      if (mode === 'admin-template') {
        return `/preview/template/${previewTemplate?.id}?sectionId=${nextSectionId}`;
      }

      return `/questionnaire/${selectedCompanyHash}?questionnaireId=${selectedQuestionnaire?.id}&sectionId=${nextSectionId}`;
    };

    const nextUrl = _getNextUrl();

    return push(nextUrl);
  };

  const onClientSubmitQuestionnaire = async () => {
    try {
      setLoading(true);

      const response = await submitQuestionnaire({
        questionnaireId: questionnaire?.id!,
        companyId: companyId!,
        token,
      });

      if (mode === 'client') {
        setNextQuestionnaire(response);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setConfirming(false);
      setSubmitted(true);
      setLoading(false);
    }
  };

  const onToggleSubmitted = () => push('/');

  const onToggleConfirm = () => {
    if (isPending) {
      if (mode === 'admin-preview') {
        return setShowIncompletePreviewNavigation(true);
      }
      if (mode === 'admin-template') return;

      return setShowIncompleteNavigation(true);
    }

    setShowIncompleteNavigation(false);
    setShowIncompletePreviewNavigation(false);

    return setConfirming(!isConfirming);
  };

  const onNextQuestionnaire = () => {
    if (!nextQuestionnaire) return;

    push(
      `/questionnaire/${nextQuestionnaire?.hash_id}?questionnaireId=${nextQuestionnaire?.questionnaire_id}`,
    );

    setTimeout(() => {
      setSubmitted(false);
      setConfirming(false);
      setReviewed(false);
    }, 100);
  };

  return (
    <>
      <Dialog isNarrow={true} isOpen={isSubmitted} onClose={onToggleSubmitted}>
        <Dialog.Content>
          <Flex isColumn={true} gap={6} padding={6} isCentered={true}>
            <SuccessQuestionnaireSubmittedImage />
            <Text type="small">
              You&apos;ve successfully completed{' '}
              {selectedQuestionnaire?.name || ''} Questionnaire!
            </Text>
            {nextQuestionnaire?.questionnaire_id && (
              <Flex isColumn={true} isCentered={true} gap={4}>
                <Separator isLight={true} />
                <Text type="small">
                  It looks like you have another questionnaire to work on:{' '}
                  {nextQuestionnaire?.name}.
                </Text>
              </Flex>
            )}
          </Flex>
          <ButtonGroup isFullWidth={true}>
            {nextQuestionnaire?.questionnaire_id && (
              <Button
                intent="primary"
                value="Start the next item"
                onClick={onNextQuestionnaire}
              />
            )}
            <Button
              intent={
                nextQuestionnaire?.questionnaire_id ? 'secondary' : 'primary'
              }
              value="Back to Dashboard"
              onClick={onToggleSubmitted}
            />
          </ButtonGroup>
        </Dialog.Content>
      </Dialog>
      <Dialog
        isNarrow={true}
        isOpen={isConfirming}
        title="Confirm Questionnaire Submission"
        onClose={onToggleConfirm}
      >
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <Alert type="info" icon="info">
              Once you submit this questionnaire, you won&apos;t be able to make
              changes. If you need to make updates or provide additional
              information after submitting, please send us a message.
            </Alert>
            {mode === 'admin-template' && (
              <Alert type="warning">
                Template Preview cannot be submitted.
              </Alert>
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            <Button
              intent="danger-secondary"
              value="Cancel"
              onClick={onToggleConfirm}
            />
            <Button
              intent="primary"
              isLoading={isLoading}
              value="I understand"
              onClick={onClientSubmitQuestionnaire}
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
      {!isDisabled && (
        <Flex
          isBetween={true}
          className={styles.questionnaireFooter}
          isYCentered={true}
          padding={8}
        >
          <ButtonGroup>
            {sectionsCount > 1 && (
              <Button
                value="Previous"
                intent="secondary"
                iconLeft="arrow-left"
                onClick={onClientPreviousSection}
                isDisabled={isPreviousDisabled || isQuestionnaireLoading}
              />
            )}
          </ButtonGroup>
          <ButtonGroup>
            <Button
              intent={mode === 'client' ? 'primary' : 'secondary'}
              value={isSubmitSection ? 'Submit' : 'Next'}
              iconRight={isSubmitSection ? 'check-circle' : 'arrow-right'}
              onClick={isSubmitSection ? onToggleConfirm : onClientNextSection}
              isDisabled={!isSubmittable || isQuestionnaireLoading}
              isLoading={isLoading}
            />
          </ButtonGroup>
        </Flex>
      )}
    </>
  );
};
